import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  AlertColor,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import NewOperatorModal from "./NewOperatorModal";
import EditOperatorModal from "./EditOperatorModal";
import BackButton from "../../atoms/Button/BrowserBackButton";
import BuildingEditForm from "./BuildingEditForm";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import { CustomContainer } from "../../../styles/styles";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import ConfirmDialog from "../../molecules/ConfirmDialog";
import {
  emptyOperator,
  OperatorType,
  operatorTypeToDisplayText,
} from "./OperatorListPage";
import mirairodoorIcon from "../../../assets/images/ftsmartkey_icon_small.png";
import ftsmartkeyIcon from "../../../assets/images/mirairodoor_icon_small.png";

interface FulltechOffice {
  autoFulltechOfficeId: number;
  officeName: string;
}

interface Operator {
  autoOperatorId: number;
  operatorId: string;
  password: string;
  operatorName: string;
  departmentName: string;
  operatorPhoneNumber: string;
  operatorEmail: string;
  memo: string;
  operatorType: OperatorType;
  createdAt: string;
}

interface Building {
  autoBuildingId: number;
  buildingId: string;
  buildingManagementId: string;
  buildingName: string;
  buildingAddress: string;
  fulltechOffice: FulltechOffice;
  operators: Operator[];
  memo: string;
}

const BuildingEditPage: React.FC = () => {
  const { autoBuildingId = "" } = useParams();

  const [searchTerm, setSearchTerm] = useState("");

  const [fetchData, setFetchData] = useState<Building>();
  const [buildingMemo, setBuildingMemo] = useState("");

  const [operators, setOperators] = useState<Operator[]>([]);

  const [allOperators, setAllOperators] = useState<Operator[]>([]);

  const [operatorAddOpen, setOperatorAddOpen] = useState(false);
  const [newOperator, setNewOperator] = useState<Operator>(emptyOperator);
  const [operatorSelectionOpen, setOperatorSelectionOpen] = useState(false);
  const [editOperatorOpen, setEditOperatorOpen] = useState(false);
  const [editOperator, setEditOperator] = useState<Operator>(emptyOperator);

  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 確認ダイアログ
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedOperatorIdOnDeletion, setSelectedOperatorIdOnDeletion] =
    React.useState<number | undefined>(undefined);
  const [newOperatorConfirmOpen, setNewOperatorConfirmOpen] =
    React.useState(false);
  // 新しいオペレータのパスワードを保存するためのステートを追加します。
  const [newOperatorPassword, setNewOperatorPassword] =
    React.useState<string>("");

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");

  useEffect(() => {
    setLoading(true); // ローディング開始

    // 内部の非同期関数を定義
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call showBuilding API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT + `/buildings/${autoBuildingId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          }
        );

        const data: Building = await response.json();
        console.log("フェッチデータ" + JSON.stringify(data));
        setFetchData(data);
        setBuildingMemo(data.memo);
        setOperators(data.operators);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false); // ローディング終了
      }
    };

    // 非同期関数を実行
    fetchData();
  }, [autoBuildingId]);

  // オペレータ一覧取得API
  useEffect(() => {
    // 内部の非同期関数を定義
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call indexOperators API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT + "/operators",
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          }
        );

        const data = await response.json();
        console.log("オペレータ一覧データ" + JSON.stringify(data));
        setAllOperators(data.operators);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    // 非同期関数を実行
    fetchData();
  }, []);

  // 新しいオペレータを現在のオペレータ一覧に追加する関数。
  const handleNewOperator = async () => {
    setOperators([...operators, newOperator]);

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    // APIにPOSTする
    try {
      // call storeOperatorWithAutoBuildingId API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/buildings/${autoBuildingId}/operators`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            operatorName: newOperator.operatorName,
            departmentName: newOperator.departmentName,
            operatorPhoneNumber: newOperator.operatorPhoneNumber,
            operatorEmail: newOperator.operatorEmail,
            memo: newOperator.memo,
            operatorType: newOperator.operatorType,
          }),
        }
      );

      const data = await response.json();
      console.log("New Operator Creation Success:", data);

      // パスワードを保存します。
      setNewOperatorPassword(data.password);

      setNewOperatorConfirmOpen(true);
    } catch (error) {
      console.error("New Operator Creation Error:", error);
    }

    // モーダルを閉じて、新規オペレーターデータをリセットする
    setNewOperator(emptyOperator);
    setOperatorAddOpen(false);
  };

  // 新しいオペレータ追加ダイアログを開く関数。
  const handleOpenDialog = () => {
    setOperatorAddOpen(true);
  };

  // 新しいオペレータ追加ダイアログを閉じる関数。
  const handleCloseDialog = () => {
    setOperatorAddOpen(false);
  };

  // 新しいオペレータフォームの値が変更されたときに呼ばれる関数。新しいオペレータオブジェクトを更新します。
  const handleNewOperatorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewOperator({ ...newOperator, [event.target.name]: event.target.value });
  };

  // ダイアログでオペレーターを選択する関数。選択したオペレータを現在のオペレータ一覧に追加します。
  const handleSelectOperator = async (operator: any) => {
    setOperators([...operators, operator]);
    setOperatorSelectionOpen(false);

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    // Post to API
    try {
      // call attachOperator API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/buildings/${autoBuildingId}/attach-operator`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({ autoOperatorId: operator.autoOperatorId }),
        }
      );

      const data = await response.json();
      console.log("物件にオペレーターの紐付け成功:", data);
    } catch (error) {
      console.error("物件にオペレーターの紐付けError:", error);
    }
  };

  // オペレータの削除を処理する関数。指定したオペレータIDを持つオペレータを現在のオペレータ一覧から削除します。
  const handleDeleteOperator = async (operatorId: number) => {
    setDeleteLoading(true); // ローディング開始

    try {
      const jwtToken = await getJwtTokenFromIdToken();

      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/buildings/${autoBuildingId}/detach-operator`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({ autoOperatorId: operatorId }),
        }
      );

      const data = await response.json();
      console.log("物件からオペレーターの削除成功：", data);
      setSnackbarMessage("物件からオペレーターの削除成功");
      setAlertSeverity("success");
      setSnackbarOpen(true);

      setOperators(
        operators.filter((operator) => operator.autoOperatorId !== operatorId)
      );
    } catch (error) {
      console.error("物件からオペレーターの削除エラー：", error);
      setSnackbarMessage("物件からオペレーターの削除エラー");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeleteLoading(false); // ローディング終了
      setDeleteConfirmOpen(false); // モーダルを閉じる
    }
  };

  //オペレータ編集ダイアログを開く関数。
  const handleEditOperatorOpen = (operator: any) => {
    setEditOperator(operator);
    setEditOperatorOpen(true);
  };

  //オペレータ編集ダイアログを閉じる関数。
  const handleEditOperatorClose = () => {
    setEditOperatorOpen(false);
  };
  //編集中のオペレータの情報が変更されたときに呼ばれる関数。編集中のオペレータオブジェクトを更新します。
  const handleEditOperatorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (editOperator.autoOperatorId > 0) {
      setEditOperator({
        ...editOperator,
        [event.target.name]: event.target.value,
      });
    }
  };

  // オペレータ編集フォームが送信されたときに呼ばれる関数。オペレータの情報を更新し、ダイアログを閉じます。
  const handleEditOperatorSubmit = async () => {
    const newOperators = operators.map((operator) => {
      if (operator.autoOperatorId === editOperator.autoOperatorId) {
        return editOperator;
      } else {
        return operator;
      }
    });
    setOperators(newOperators);

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();

    try {
      // call updateOperator API
      const response = await fetch(
        process.env.REACT_APP_API_ENDPOINT +
          `/operators/${editOperator.autoOperatorId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: jwtToken,
          },
          body: JSON.stringify({
            operatorName: editOperator.operatorName,
            departmentName: editOperator.departmentName,
            operatorPhoneNumber: editOperator.operatorPhoneNumber,
            operatorEmail: editOperator.operatorEmail,
            memo: editOperator.memo,
          }),
        }
      );

      const data = await response.json();
      console.log("Operator Update Success:", data);
      setSnackbarMessage("オペレーターの更新が完了しました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);
      setEditOperatorOpen(false); // <- Snackbar表示後にモーダルを閉じる
    } catch (error) {
      console.error("Operator Update Error:", error);
      setSnackbarMessage("オペレーターの更新に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      setEditOperatorOpen(false); // <- Snackbar表示後にモーダルを閉じる
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  return (
    <CustomContainer maxWidth={false}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <PageTitle title="物件詳細・編集" />
              <BackButton />
            </Box>
          </Grid>
          <Grid item xs={7}>
            <BuildingEditForm
              building={{
                buildingId: fetchData?.buildingId || "",
                buildingManagementId: fetchData?.buildingManagementId || "",
                buildingName: fetchData?.buildingName || "",
                buildingAddress: fetchData?.buildingAddress || "",
                officeName: fetchData?.fulltechOffice?.officeName || "",
                memo: fetchData?.memo || "",
              }}
              buildingMemo={buildingMemo}
              onMemoChange={(e) => setBuildingMemo(e.target.value)}
              autoBuildingId={autoBuildingId}
              setSnackbarOpen={setSnackbarOpen}
              setAlertSeverity={setAlertSeverity}
              setSnackbarMessage={setSnackbarMessage}
            />
          </Grid>
          <Grid item xs={1}></Grid> {/* スペース調整*/}
          <Grid item xs={4}></Grid> {/* スペース調整*/}
          <Grid item xs={12}>
            <Typography variant="h6" marginBottom={2}>
              オペレーター一覧
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>サービス種別</TableCell>
                    <TableCell>オペレーターID</TableCell>
                    <TableCell>氏名(部署名)</TableCell>
                    <TableCell>部署名(会社名) </TableCell>
                    <TableCell>メモ</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operators?.map((operator, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {operatorTypeToDisplayText(operator.operatorType)}
                      </TableCell>
                      <TableCell>{operator.operatorId}</TableCell>
                      <TableCell>{operator.operatorName}</TableCell>
                      <TableCell>{operator.departmentName} </TableCell>
                      <TableCell>{operator.memo}</TableCell>
                      <TableCell>
                        <IconButtonWithTooltip
                          title={"編集"}
                          onClick={() => handleEditOperatorOpen(operator)}
                          icon={<EditIcon />}
                        ></IconButtonWithTooltip>
                        <IconButtonWithTooltip
                          title={"削除"}
                          onClick={() => {
                            setDeleteConfirmOpen(true);
                            setSelectedOperatorIdOnDeletion(
                              operator.autoOperatorId
                            );
                          }}
                          icon={<DeleteIcon />}
                        ></IconButtonWithTooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Dialog
              open={deleteConfirmOpen}
              onClose={() => setDeleteConfirmOpen(false)}
            >
              <DialogTitle>確認</DialogTitle>
              <DialogContent>
                <Typography>
                  この物件からこのオペレーターを削除しますか？
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setDeleteConfirmOpen(false)}
                  color="primary"
                  disabled={deleteLoading}
                >
                  キャンセル
                </Button>
                <Button
                  onClick={() => {
                    if (selectedOperatorIdOnDeletion !== undefined) {
                      handleDeleteOperator(selectedOperatorIdOnDeletion);
                    }
                  }}
                  color="primary"
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CircularProgress size={24} /> : "OK"}
                </Button>
              </DialogActions>
            </Dialog>

            <Box display="flex" justifyContent="flex-end" marginTop={2}>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                color="primary"
                onClick={() => setOperatorSelectionOpen(true)}
              >
                登録済みオペレーターを追加
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenDialog}
                sx={{ marginLeft: 1 }}
              >
                新規登録
              </Button>
              {/* <Button variant="outlined" color="primary" >保存</Button> */}
            </Box>
          </Grid>
        </Grid>
      )}
      <Dialog
        open={operatorSelectionOpen}
        onClose={() => setOperatorSelectionOpen(false)}
      >
        <DialogTitle>オペレーターの選択</DialogTitle>
        <DialogContent>
          <TextField
            label="氏名（部署名）検索"
            type="search"
            fullWidth
            margin="dense"
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <List>
            {allOperators
              .filter(
                (operator) =>
                  !operators?.some(
                    (addedOperator) =>
                      addedOperator.autoOperatorId === operator.autoOperatorId
                  )
              ) // 追加済みオペレーターを除外
              .filter((operator) => operator.operatorName.includes(searchTerm))
              .map((operator, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    button
                    onClick={() => handleSelectOperator(operator)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "10px 16px",
                    }}
                  >
                    {/* アイコンの表示 */}
                    <Tooltip
                      title={
                        operator.operatorType === "autolock"
                          ? "FTスマートキー"
                          : operator.operatorType === "social"
                          ? "ミライロドア"
                          : ""
                      }
                    >
                      <Box
                        component="img"
                        src={
                          operator.operatorType === "autolock"
                            ? mirairodoorIcon
                            : operator.operatorType === "social"
                            ? ftsmartkeyIcon
                            : undefined
                        }
                        alt="Icon"
                        sx={{ width: 32, height: 32, borderRadius: "10%" }}
                      />
                    </Tooltip>

                    <ListItemText
                      primary={`氏名（部署名）: ${operator.operatorName}`}
                      secondary={`部署名（会社名）: ${operator.departmentName}`}
                      primaryTypographyProps={{
                        variant: "body1",
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        noWrap: true,
                        color: "textSecondary",
                      }}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOperatorSelectionOpen(false)}
            color="primary"
          >
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
      <NewOperatorModal
        open={operatorAddOpen}
        handleClose={handleCloseDialog}
        handleNewOperator={handleNewOperator}
        handleNewOperatorChange={handleNewOperatorChange}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />
      <EditOperatorModal
        open={editOperatorOpen}
        handleClose={handleEditOperatorClose}
        handleEditOperatorSubmit={handleEditOperatorSubmit}
        handleEditOperatorChange={handleEditOperatorChange}
        editOperator={editOperator}
        setEditOperator={setEditOperator}
        setSnackbarOpen={setSnackbarOpen}
        setAlertSeverity={setAlertSeverity}
        setSnackbarMessage={setSnackbarMessage}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={newOperatorConfirmOpen}
        onClose={() => {
          setNewOperatorConfirmOpen(false);
        }}
        onConfirm={() => {
          window.location.reload();
        }}
        title="確認"
        description={
          "オペレーターの作成が完了しました。パスワード：" + newOperatorPassword
        }
        showCancelButton={false}
      />
    </CustomContainer>
  );
};

export default BuildingEditPage;
